import { useMessage } from '@/providers';
import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountAvatar, Button, Col, Row, Space } from '..';
import { DnD } from './DnD';

const MAXIMUM_FILE_SIZE = 10 * 1024 ** 2;

type Props = {
  iconUrl: string;
  onChange?: (imgUrl: string) => void;
  onSave?: () => void;
  onClear?: () => void;
  disabled?: boolean;
};

export const AccountIconUploader: FC<Props> = ({
  iconUrl,
  onChange,
  onSave,
  onClear,
  disabled = false,
}) => {
  const { openMessage } = useMessage();
  const { t } = useTranslation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: (migrate from ESLint)
  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (acceptedFiles.length === 0) {
        await openMessage('error', t('error-message-file-not-exist'));
        return;
      }
      const file = acceptedFiles[0];
      if (file.type.indexOf('image/') === -1) {
        await openMessage('error', t('error-message-invalid-file-type'));
        return;
      }
      if (file.type === 'image/heic') {
        await openMessage('error', t('error-message-not-permitted-image-type'));
        return;
      }
      if (file.size > MAXIMUM_FILE_SIZE) {
        await openMessage(
          'error',
          t('error-message-too-large-file-size', { maxSize: '10MB' }),
        );
        return;
      }

      const reader = new FileReader();

      reader.onloadend = async (event) => {
        if (!event.target?.result) {
          await openMessage('error', t('unknown-error'));
          return;
        }
        const base64ImageString: string =
          typeof event.target.result === 'string'
            ? event.target.result
            : Buffer.from(event.target.result).toString();

        onChange?.(base64ImageString);
      };
      if (file) {
        reader.readAsDataURL(file);
      }
    },
    [onChange],
  );

  return (
    <Row align="middle" gutter={[8, 0]}>
      <Col span={18}>
        <DnD onDrop={onDrop} disabled={disabled || !onChange} />
      </Col>
      <Col span={6}>
        <Space direction="vertical" align="center">
          <AccountAvatar size={96} iconUrl={iconUrl} />
          <Space>
            <Button
              size="small"
              type="primary"
              onClick={onSave}
              disabled={disabled || !onSave}
            >
              {t('save')}
            </Button>
            <Button
              style={{ width: 72 }}
              size="small"
              onClick={onClear}
              disabled={disabled || !onClear}
            >
              {t('reset')}
            </Button>
          </Space>
        </Space>
      </Col>
    </Row>
  );
};
